import { Direction, FrenzyAxis, FrenzyLine, LeptonDirection, LeptonState, Point, diagonalDirectionForLabel, directionForLabel } from "../Model";
import { describeLepton, isInRange, minMax, pointsToLine } from "../Utils";
import { HitBox, describeHitBox } from "./HitBox";
import { areSame } from "./Point";

export const length = (line: FrenzyLine) => {
  const [minX, maxX] = minMax(line.start[0], line.end[0]);
  const [minY, maxY] = minMax(line.start[1], line.end[1]);

  return (maxX - minX) + (maxY - minY);
};

export const isInXRange = (line: FrenzyLine, x: number):boolean => {
  return isInRange(line.start[0], line.end[0], x)
}

export const isInYRange = (line: FrenzyLine, y: number):boolean => {
  return isInRange(line.start[1], line.end[1], y)
}

export const points = (line: FrenzyLine, scale: number = 1) => {
	return [line.start[0] * scale, line.start[1] * scale, line.end[0] * scale, line.end[1] * scale];
};

export const lengthOfAllLines = (lines: FrenzyLine[]) => {
  return lines.reduce((acc, line) => {
    return acc + length(line);
  }, 0);
};

export function describeLine(l: FrenzyLine | null): string {
  if (!l) {
    return "[NO LINE"
  }
  return `[${l.direction.label} - ${l.start[0]},${l.start[1]} to ${l.end[0]},${l.end[1]}]`;
};

export const describeLines = (lines: FrenzyLine[]) => {
  return lines.reduce((acc, l) => {
    return acc + `${describeLine(l)},`;
  }, `(${lines.length})`);
};

export function areSameLine(lineA: FrenzyLine, lineB: FrenzyLine):boolean {
  return (areSame(lineA.start, lineB.start) && areSame(lineA.end, lineB.end)) ||
          (areSame(lineA.end, lineB.start) && areSame(lineA.start, lineB.end));
}

export const isOnVerticalLine = (y: number, line: FrenzyLine) => {
	if (line.direction.label === 'UP') {
    // console.log(`up match y: ${y}: ${describeLine(line)} - ${((line.end[1] <= y) && (line.start[1] >= y))}`)
		return ((line.end[1] <= y) && (line.start[1] >= y));
	} 

  // console.log(`down match y: ${y}: ${describeLine(line)} - ${((line.start[1] <= y) && (line.end[1] >= y))}`)

	return ((line.start[1] <= y) && (line.end[1] >= y));
}

export function normaliseToRight(hLine: FrenzyLine): FrenzyLine {
  const { direction, start, end } = hLine;
  if (direction.label === 'RIGHT') return hLine;

  return {
    direction: Direction.RIGHT,
    start: end,
    end: start
  }
}

export function reverse(line: FrenzyLine): FrenzyLine {
  const { direction, start, end } = line;
  return {
    direction: directionForLabel(direction.oppositeLabel),
    start: end,
    end: start
  }
}

// Remove the given line from the given lines, returning the new reduced array
export function subtract(line: FrenzyLine, allLines: Array<FrenzyLine>): Array<FrenzyLine> {
  return allLines.filter(l => !areSameLine(line, l));
}

export function lineContainsSegment(line: FrenzyLine, segment: FrenzyLine): boolean {
  if (line.direction.axis !== segment.direction.axis) return false;

  return (line.direction.axis.intersects(segment.start[0], segment.start[1], line))
    && (line.direction.axis.intersects(segment.end[0], segment.end[1], line));
}

export function findLinesThatStartOrEndAt(allLines: Array<FrenzyLine>, point: Point, axis?: FrenzyAxis): Array<FrenzyLine> {
  const haveSameOrigin = allLines.filter((line) => {
    return (areSame(line.start, point) || areSame(line.end, point));
  });

  if (axis) {
    return haveSameOrigin.filter((line) => line.direction.axis === axis)
  }
  return haveSameOrigin;
}

export function topmostYOfLine(line:FrenzyLine):number {
  return minMax(line.start[1], line.end[1])[0]
}
export function topmostPointOfLine(line:FrenzyLine):Point {
  const { start, end } = line;
  return (start[1] < end[1]) ? start : end;
}
export function rightmostPointOfLine(line:FrenzyLine):Point {
  const { start, end } = line;
  return (start[0] > end[0]) ? start : end;
}

export function buildLinesToJoinPoints(points: Array<Point>, closed = false):Array<FrenzyLine> {
  const lines: Array<FrenzyLine> = [];

  for (let i = 0; i < points.length -1; i++) {
    const newLine = pointsToLine(points[i], points[i+1]);
    lines.push(newLine);
  }

  if (closed) {
    const closingLine = pointsToLine(points[points.length -1], points[0]);
    lines.push(closingLine);
  }

  return lines;
}

// The reflection that "doesn't matter" is the one that stretches away from the intersection point
// If we're arriving in direction SE, and we hit a corner like this ----
//                                                                     |
// then we should bounce off the horizontal axis, but the vertical axis is irrelevant.
//
// Example 2:
// Wall is vertical but with a line heading West, e.g: |
//                                                 ----|
//                                                     | H	
//                                                     |   L
// Lepton is arriving in a NW direction (Head of lepton shown with an H)
// The Westerly line doesn't matter. Why?
// For a projectile heading NW, things on the W side of a N/S line are unimportant.
// Does this hold for example 1?
// Heading SE, and I'm N of an E/W line with an S direction line that is unimportant
// So to generalise 
// we should filter intersectingLines to ones significant to reflection
// where significant is:
// extends INTO the lepton's current field
// 
export function findIntersectingLineOfSignificance (lines: Array<FrenzyLine>, leptonState: LeptonState):FrenzyLine {
  if (lines.length === 1) {
    return lines[0];
  }

  const remainingLines = applyIrrelevantBounceLineFilter(lines, leptonState);

  if (remainingLines.length === 0) {
    console.warn(`Failed to determine bounce line from ${describeLines(lines)} and ${describeLepton(leptonState)}`);
    return lines[0];
  } else if (remainingLines.length > 1) {
    console.warn(`Multiple bounces lines from ${describeLines(lines)} and ${describeLepton(leptonState)} => ${describeLines(remainingLines)}`);
  }

  return remainingLines[0];
}

export function applyIrrelevantBounceLineFilter(lines: Array<FrenzyLine>, leptonState: LeptonState) {
  const { x, y, direction } = leptonState;  
  const filterFn = direction.irrelevantBounceLineFilter([x,y]);
  return lines.filter(l => !filterFn(l));
}

export function findIntersectingLineOfSignificanceForHitBox(
  lines: Array<FrenzyLine>, 
  leptonState: LeptonState,
  leptonHitBox: HitBox
  ):FrenzyLine {
    const { minX, minY, maxX, maxY } = leptonHitBox;
    const { leptonId, x, y, direction } = leptonState;
    // OMFG - test every pixel in the hitbox
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        const testLeptonState = { leptonId, x, y, direction};
        // console.log(`testLeptonState: ${JSON.stringify(testLeptonState, null, 2)}`)

        const result = applyIrrelevantBounceLineFilter(lines, testLeptonState);
        if (result.length === 1) {
          // console.log(`Found a single line at ${x},${y} ${describeLine(result[0])} of ${describeLines(lines)}`);
          return result[0];
        } else {
          // console.log(`Found ${result.length} relevant lines at ${x},${y}`);
          if (result.length === 0) {
            // they were ALL knocked out. Just pick one
            return lines[0];
          }
        }
      }
    }
    console.warn(`Found no line for ${describeLepton(leptonState)} with hitbox ${describeHitBox(leptonHitBox)} in lines ${describeLines(lines)}`);
    return lines[0];
  }
  

  /** Join continuous runs of Points into lines; only for horizontal lines */
export function vectorizeIntoHorizontalLines(points: Point[]): Array<FrenzyLine> {
  const lines: Array<FrenzyLine> = []
  let runStart = -1;
  let runEnd = -1;


  if (points.length === 0) {
    return []
  } 
  
  const y = points[0][1];

  // Dedupe for unique xs and ensure they are sorted low -> high
  const onlyXs = points.map((p) => p[0]);
  const xSet = new Set(onlyXs);
  const sortedXs = Array.from(xSet).sort((p1, p2) => p1 - p2);

  sortedXs.forEach((x) => {
    if (runStart === -1) {
      runStart = x;
      runEnd = x;
      // console.log(`Run start at ${x}, ${y}`)
    } else if (x === (runEnd + 1)) {
      // extend the run
      runEnd = x;
      // console.log(`Run extended to at ${runStart} -> ${runEnd}`)

    } else {
      // console.log(`Run done at ${JSON.stringify([runStart,y,runEnd,y], null, 2)}`)
      // That is it for the run
      const start = [runStart, y] as Point;
      const end = [runEnd, y] as Point;
      const newLine = {
        start,
        end,
        direction: Direction.RIGHT
      }

      lines.push(newLine);
      runStart = x;
      runEnd = x;
      // console.log(`Run start at ${x}, ${y}`)
    }
  })

  if (runStart !== -1) {
    // console.log(`Ran out of points at ${JSON.stringify([runStart,y,runEnd,y], null, 2)}`)

    const start = [runStart, y] as Point;
    const end = [runEnd, y] as Point;
    const newLine = {
      start,
      end,
      direction: Direction.RIGHT
    }
    lines.push(newLine)
  }
  
  return lines
}

  /** Join continuous runs of Points into lines; only for vertical lines */
export function vectorizeIntoVerticalLines(points: Point[]): Array<FrenzyLine> {
    const lines: Array<FrenzyLine> = []
    let runStart = -1;
    let runEnd = -1;
  
  
    if (points.length === 0) {
      return []
    } 
    
    const x = points[0][0];
  
    // Dedupe for unique xs and ensure they are sorted low -> high, i.e. DOWN
    const onlyYs = points.map((p) => p[1]);
    const ySet = new Set(onlyYs);
    const sortedYs = Array.from(ySet).sort((p1, p2) => p1 - p2);
  
    sortedYs.forEach((y) => {
      if (runStart === -1) {
        runStart = y;
        runEnd = y;
        // console.log(`Run start at ${x}, ${y}`)
      } else if (y === (runEnd + 1)) {
        // extend the run
        runEnd = y;
        // console.log(`Run extended to at ${runStart} -> ${runEnd}`)
  
      } else {
        // console.log(`Run done at ${JSON.stringify([runStart,y,runEnd,y], null, 2)}`)
        // That is it for the run
        const start = [x, runStart] as Point;
        const end = [x, runEnd] as Point;
        const newLine = {
          start,
          end,
          direction: Direction.DOWN
        }
  
        lines.push(newLine);
        runStart = y;
        runEnd = y;
        // console.log(`Run start at ${x}, ${y}`)
      }
    })
  
    if (runStart !== -1) {
      // console.log(`Ran out of points at ${JSON.stringify([runStart,y,runEnd,y], null, 2)}`)
  
      const start = [x, runStart] as Point;
      const end = [x, runEnd] as Point;
      const newLine = {
        start,
        end,
        direction: Direction.DOWN
      }
      lines.push(newLine)
    }
    
    return lines
  }
  

